import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Eye, Trash2 } from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import ConfirmModal from "../../components/ConfirmModal";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";

/* ------------------ Types ------------------ */
type Gender = "Male" | "Female" | "Other";

interface Contact {
  id: number;
  salutation?: string;
  name: string;
  phone?: string;
  gender?: Gender;
  dob?: string;
  approx_age?: number | string | null;
  approx_age_indicator?: boolean;
  email?: string;
  address?: string;
  type?: "EMPLOYEE" | "PATIENT";
}

/* ------------------ Component ------------------ */
const calculateAge = (dob?: string) => {
  if (!dob) return "";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age--;
  return age >= 0 ? String(age) : "";
};

const formatAge = (contact: Contact) => {
  const ageFromDob = calculateAge(contact.dob);
  if (ageFromDob) return ageFromDob;

  const approximateAge = contact.approx_age;
  if (approximateAge !== undefined && approximateAge !== null && String(approximateAge).trim()) {
    return String(approximateAge);
  }

  return "-";
};

const ContactsList: React.FC = () => {
  const [items, setItems] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState<{
    name?: string;
    phone?: string;
  }>({});

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    id?: number;
  }>({ open: false });

  const { can } = useAuth();
  const canManageContacts = can("contacts.edit");

  /* ------------------ Load ------------------ */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/contacts/`, {
          credentials: "include",
        });
        if (res.ok) {
          const contacts = await res.json();
          setItems(contacts.filter((contact: Contact) => contact.type === "PATIENT"));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* ------------------ Handlers ------------------ */
  const handleFilter = (k: string, v: string) =>
    setFilters((p) => ({ ...p, [k]: v }));

  const openDeleteConfirm = (id: number) =>
    setConfirmDelete({ open: true, id });

  const handleDelete = async (id?: number) => {
    setConfirmDelete({ open: false });
    if (!id) return;

    const res = await fetch(`${BACKEND_URL}/api/contacts/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (res.ok) {
      setItems((p) => p.filter((x) => x.id !== id));
      setModal({
        open: true,
        title: "Deleted",
        message: "Contact deleted successfully",
      });
    }
  };

  /* ------------------ Filters ------------------ */
  const filteredItems = useMemo(() => {
    return items.filter((c) => {
      if (
        filters.name &&
        !c.name.toLowerCase().includes(filters.name.toLowerCase())
      )
        return false;

      if (filters.phone && !c.phone?.includes(filters.phone)) return false;

      return true;
    });
  }, [items, filters]);

  /* ------------------ UI ------------------ */
  return (
    <SidebarLayout title="Contacts">
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <Modal
            open={modal.open}
            title={modal.title}
            message={modal.message}
            onClose={() => setModal({ ...modal, open: false })}
          />

          <ConfirmModal
            open={confirmDelete.open}
            message="Delete this contact?"
            onCancel={() => setConfirmDelete({ open: false })}
            onConfirm={() => handleDelete(confirmDelete.id)}
          />

          {/* Filters */}
          <div className="bg-surface-light p-4 rounded border">
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                className="p-2 border rounded"
                placeholder="Search name"
                value={filters.name || ""}
                onChange={(e) => handleFilter("name", e.target.value)}
              />

              <input
                className="p-2 border rounded"
                placeholder="Search phone"
                value={filters.phone || ""}
                onChange={(e) => handleFilter("phone", e.target.value)}
              />

            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left">
                <thead>
                  <tr className="text-xs text-subtext-light">
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Phone</th>
                    <th className="p-2 text-left">Age</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredItems.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="p-2 font-medium">{[c.salutation, c.name].filter(Boolean).join(" ")}</td>
                      <td className="p-2">{c.phone || "-"}</td>
                      <td className="p-2">{formatAge(c)}</td>

                      <td className="p-2">
                        <div className="flex gap-2">
                          {/* VIEW */}
                          <Link
                            to="/contacts/new"
                            state={{ contact: c, view: true }}
                            className="hover:bg-primary/10 p-1.5 rounded"
                          >
                            <Eye size={18} />
                          </Link>

                          {/* EDIT */}
                          {canManageContacts && (
                            <Link
                              to="/contacts/new"
                              state={{ contact: c }}
                              className="hover:bg-primary/10 p-1.5 rounded"
                            >
                              <Pencil size={18} />
                            </Link>
                          )}

                          {/* DELETE */}
                          {canManageContacts && (
                            <button
                              onClick={() => openDeleteConfirm(c.id)}
                              className="p-1.5 text-alert-red"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </SidebarLayout>
  );
};

export default ContactsList;
